/**
 * File Handler Page Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A comprehensive file handling page with advanced features:
 * - Multi-format file ID support (legacy GUIDs, repository slugs, news documents)
 * - News document link processing and redirection
 * - Unified file route API integration
 * - Error handling with user-friendly messages
 * - Loading states and progress indication
 * - Automatic cleanup of blob URLs
 * - File download and redirect management
 * - Repository document link handling
 * 
 * Supported URL Formats:
 * - Legacy: /file?id=guid (traditional file access)
 * - Repository: /files/detail/slug (repository document access)
 * - News Document: /files/detail/news/articleId/filename (news-linked documents)
 * 
 * Features:
 * - News article detection and type determination
 * - Automatic redirection to appropriate news article pages
 * - File existence validation and error handling
 * - Loading spinners with progress indication
 * - Error messages with navigation options
 * - Blob URL management and cleanup
 * - File download success confirmation
 * - Repository feature development notices
 * 
 * News Document Processing:
 * - Detects submitted vs weekly article types
 * - Redirects to correct news article with document parameter
 * - Handles article validation and existence checks
 * - Fallback logic for article type determination
 * 
 * Error Handling:
 * - User-friendly error messages
 * - File not found scenarios
 * - Repository access limitations
 * - Navigation options (back, news room)
 * - Development feature notifications
 * 
 * @returns {JSX.Element} File handler page with loading, error, and success states
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import apiHelper from '../utils/apiHelper';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import errorLogger from '../utils/errorLogger';

function File() {
    const [searchParams] = useSearchParams();
    const params = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [fileInfo, setFileInfo] = useState(null);

    // Handle multiple formats:
    // - Legacy: /file?id=guid
    // - Repository: /files/detail/slug  
    // - News Document: /files/detail/news/articleId/filename
    const fileId = searchParams.get('id') || params.slug;
    const articleId = params.articleId;
    const filename = params.filename;

    useEffect(() => {
        const loadFile = async () => {
            // Special handling for news document links: /files/detail/news/articleId/filename
            if (articleId && filename) {
                try {
                    // Fetch news data to determine article type and verify article exists
                    const response = await apiHelper.fetchJSONData('getAllNews');
                    
                    if (response && response.news && Array.isArray(response.news)) {
                        const article = response.news.find(item => 
                            String(item.id) === String(articleId)
                        );
                        
                        if (article) {
                            // Determine article type
                            let articleType = 'weekly'; // default
                            
                            if (article.isSubmittedArticle === true) {
                                articleType = 'submitted';
                            } else if (article.isWeeklyNews === true) {
                                articleType = 'weekly';
                            } else {
                                // Fallback detection
                                const hasAuthor = article.author || article.submittedAuthor || article.submittedCompany;
                                const hasWeeklyProps = article.weeklyCategory || article.weeklySection;
                                
                                if (hasAuthor && !hasWeeklyProps) {
                                    articleType = 'submitted';
                                }
                            }
                            
                            // Redirect to the news article with doc parameter
                            navigate(`/news/${articleType}/${articleId}?doc=${encodeURIComponent(filename)}`);
                            return;
                        } else {
                            throw new Error(`News article with ID ${articleId} not found`);
                        }
                    } else {
                        throw new Error('Unable to fetch news data');
                    }
                } catch (err) {
                    console.error('Error handling news document link:', err);
                    
                    // Log news document access error
                    errorLogger.logError(err, {
                        type: 'NEWS_DOCUMENT_ACCESS_ERROR',
                        articleId,
                        filename,
                        url: window.location.href
                    });
                    
                    setError(`Unable to load news article: ${err.message}`);
                    setLoading(false);
                    return;
                }
            }
            
            if (!fileId) {
                setError('No file ID provided');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                // Call the unified file route API directly
                const apiUrl = process.env.REACT_APP_API_URL;
                const authToken = await apiHelper.getAuthToken();
                
                const response = await fetch(`${apiUrl}graph/file?id=${encodeURIComponent(fileId)}`, {
                    headers: {
                        'Authorization': `Bearer ${authToken}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.details || errorData.error || 'File not found');
                }

                const result = await response.json();
                
                // Check if this is a redirect response
                if (result.success && result.action === 'redirect') {
                    navigate(result.redirectUrl);
                    return;
                }

                // If not a redirect, handle as file download (fallback)
                setError('Unexpected response format from file service');
                setLoading(false);

            } catch (err) {
                console.error('Error loading file:', err);
                
                // Enhanced error logging for file access issues
                errorLogger.logFileAccessError(fileId, err, {
                    endpoint: `/api/graph/file?id=${fileId}`,
                    userAgent: navigator.userAgent,
                    responseStatus: err.response?.status,
                    responseStatusText: err.response?.statusText
                });
                
                setError(err.message);
                setLoading(false);
            }
        };

        loadFile();

        // Cleanup blob URLs on unmount
        return () => {
            if (fileInfo?.blobUrl) {
                URL.revokeObjectURL(fileInfo.blobUrl);
            }
        };
    }, [fileId, articleId, filename, navigate]);

    // Cleanup blob URL when component unmounts
    useEffect(() => {
        return () => {
            if (fileInfo?.blobUrl) {
                URL.revokeObjectURL(fileInfo.blobUrl);
            }
        };
    }, [fileInfo?.blobUrl]);

    if (loading) {
        return (
            <div className="container mt-6">
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    minHeight: '400px',
                    textAlign: 'center'
                }}>
                    <LoadingSpinner />
                    <h3 style={{ marginTop: '20px', color: '#6c757d' }}>
                        Loading File...
                    </h3>
                    <p style={{ color: '#6c757d', marginTop: '10px' }}>
                        File ID: <code>{fileId}</code>
                    </p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mt-6">
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    minHeight: '400px',
                    textAlign: 'center'
                }}>
                    <div style={{
                        background: '#f8d7da',
                        border: '1px solid #f5c6cb',
                        borderRadius: '8px',
                        padding: '20px',
                        maxWidth: '600px',
                        width: '100%'
                    }}>
                        <h3 style={{ color: '#721c24', marginBottom: '15px' }}>
                            <i className="fas fa-exclamation-triangle"></i> 
                            {error.includes('Repository access feature') ? ' Feature Not Available' : ' File Not Found'}
                        </h3>
                        <p style={{ color: '#721c24', marginBottom: '15px' }}>
                            {error}
                        </p>
                        {error.includes('Repository access feature') && (
                            <div style={{ 
                                background: '#fff3cd', 
                                border: '1px solid #ffeaa7', 
                                borderRadius: '4px', 
                                padding: '10px', 
                                marginBottom: '15px',
                                color: '#856404'
                            }}>
                                <p style={{ margin: 0, fontSize: '14px' }}>
                                    <i className="fas fa-info-circle"></i> This appears to be a repository document link. 
                                    The repository access feature is still in development.
                                </p>
                            </div>
                        )}
                        <p style={{ color: '#721c24', marginBottom: '20px' }}>
                            File ID: <code>{fileId}</code>
                        </p>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <button 
                                className="btn btn-primary"
                                onClick={() => window.history.back()}
                            >
                                <i className="fas fa-arrow-left"></i> Go Back
                            </button>
                            <button 
                                className="btn btn-outline-primary"
                                onClick={() => window.location.href = '/newsroom'}
                            >
                                <i className="fas fa-newspaper"></i> News Room
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // This should rarely be shown as we redirect/download
    return (
        <div className="container mt-6">
            <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                minHeight: '400px',
                textAlign: 'center'
            }}>
                <div style={{
                    background: '#d4edda',
                    border: '1px solid #c3e6cb',
                    borderRadius: '8px',
                    padding: '20px',
                    maxWidth: '600px',
                    width: '100%'
                }}>
                    <h3 style={{ color: '#155724', marginBottom: '15px' }}>
                        <i className="fas fa-check-circle"></i> File Downloaded
                    </h3>
                    <p style={{ color: '#155724', marginBottom: '15px' }}>
                        The file has been downloaded to your device.
                    </p>
                    {fileInfo && (
                        <div style={{ color: '#155724', marginBottom: '20px' }}>
                            <p><strong>Filename:</strong> {fileInfo.filename}</p>
                            <p><strong>Size:</strong> {(fileInfo.size / 1024 / 1024).toFixed(2)} MB</p>
                            <p><strong>Type:</strong> {fileInfo.contentType}</p>
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                        <button 
                            className="btn btn-primary"
                            onClick={() => window.history.back()}
                        >
                            <i className="fas fa-arrow-left"></i> Go Back
                        </button>
                        <button 
                            className="btn btn-outline-primary"
                            onClick={() => window.location.href = '/newsroom'}
                        >
                            <i className="fas fa-newspaper"></i> News Room
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default File; 